import React, { useCallback, useEffect, useState } from 'react';
import { Link2, Copy, CheckCircle2, Download, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { adminAcquisitionAPI } from '../services/api';
import { buildTrackingLink } from '../utils/acquisition';

/**
 * Admin → Tracking Links
 * ──────────────────────
 * 1. Monthly sign-ups per partner tracking link (utm_source). "Qualifying
 *    hosts" is the number a per-sign-up partner fee is invoiced on — host
 *    account, verified email, started a listing, not a demo account.
 *    Each source lists its qualifying accounts so duplicates can be spotted
 *    before invoicing.
 * 2. A link builder, so every partner (and the founder) gets a correctly
 *    tagged link.
 *
 * Rules: docs/TRACKING_LINKS.md · backend/services/acquisition.py.
 * Self-contained so it can't destabilise the main dashboard.
 */
const card = 'bg-[#111827] border border-white/6 rounded-xl';
const label = 'text-[11px] font-semibold text-zinc-400 uppercase tracking-wide';
const input =
  'w-full bg-[#0b1220] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 ' +
  'placeholder-zinc-600 focus:outline-none focus:border-blue-500/60';
const th = 'px-3 py-2 text-left text-[10px] font-bold uppercase tracking-widest text-zinc-500';
const td = 'px-3 py-2 text-sm text-zinc-200 tabular-nums';

const thisMonth = () => new Date().toISOString().slice(0, 7);

// Agreed link conventions (docs/TRACKING_LINKS.md). The source is what a
// partner is credited and paid on, so keep these exact.
const PRESETS = [
  { name: 'Dev — YourGame.Media (billable)', source: 'ygm', medium: 'linkedin', campaign: '' },
  { name: "Gregory — sharing Dev's content", source: 'greg', medium: 'whatsapp', campaign: 'ygm-content' },
  { name: 'Gregory — own outreach', source: 'greg', medium: 'whatsapp', campaign: '' },
];

const PAGES = [
  { path: '/become-host', name: 'Become a host' },
  { path: '/signup', name: 'Sign up' },
  { path: '/', name: 'Home (property grid)' },
];

const toCsv = (data) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [['month', 'source', 'campaign', 'user_ref', 'name', 'email', 'signed_up'].map(esc).join(',')];
  (data?.sources || []).forEach((row) => {
    (row.qualifyingAccounts || []).forEach((a) => {
      lines.push([data.month, row.source, a.campaign || '', a.userRef || '', a.name, a.email,
        a.createdAt ? String(a.createdAt).slice(0, 10) : ''].map(esc).join(','));
    });
  });
  return lines.join('\n');
};

const AdminAcquisitionSources = ({ getToken }) => {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState({});

  const load = useCallback(async (m) => {
    setBusy(true); setError(null);
    try {
      setData(await adminAcquisitionAPI.getSummary(m, getToken()));
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load the report.');
    } finally {
      setBusy(false);
    }
  }, [getToken]);

  useEffect(() => { load(month); }, [load, month]);

  const downloadCsv = () => {
    const blob = new Blob([toCsv(data)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ariv-qualifying-signups-${data.month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── link builder ─────────────────────────────────────────
  const [page, setPage] = useState(PAGES[0].path);
  const [source, setSource] = useState(PRESETS[0].source);
  const [medium, setMedium] = useState(PRESETS[0].medium);
  const [campaign, setCampaign] = useState(PRESETS[0].campaign);
  const [copied, setCopied] = useState(false);

  let link = '';
  try {
    link = buildTrackingLink(`${window.location.origin}${page}`, { source, medium, campaign });
  } catch { /* invalid origin in odd environments */ }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Link2 size={18} className="text-blue-400" /> Tracking Links
        </h2>
        <p className="text-xs text-zinc-500 mt-1 max-w-3xl">
          Sign-ups per partner tracking link (utm_source). These links are not referral codes: they
          never earn a referral share and never count toward the referral cap (Terms §10.6).
          A partner paid per sign-up is invoiced on <strong className="text-zinc-300">Qualifying hosts</strong> for
          their own source only.
        </p>
      </div>

      {/* ── Monthly report ── */}
      <div className={`${card} p-5 space-y-4`}>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className={label}>Month (sign-up date, UTC)</div>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value || thisMonth())}
                   className={`${input} w-44 mt-1`} />
          </div>
          <button onClick={() => load(month)} disabled={busy}
                  className="px-3 py-2 text-xs font-semibold rounded-lg bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10 disabled:opacity-50">
            {busy ? 'Loading…' : 'Refresh'}
          </button>
          {data?.sources?.some((r) => r.qualifyingAccounts?.length) && (
            <button onClick={downloadCsv}
                    className="px-3 py-2 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-500 flex items-center gap-1.5">
              <Download size={13} /> Qualifying sign-ups (CSV, internal only)
            </button>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-300">
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        {data && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-white/10">
                  <tr>
                    <th className={th}>Source</th>
                    <th className={th}>Sign-ups</th>
                    <th className={th}>Hosts</th>
                    <th className={th}>Qualifying hosts</th>
                    <th className={th}>Live listing</th>
                    <th className={th}>First booking</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sources.length === 0 && (
                    <tr><td className={`${td} text-zinc-500`} colSpan={6}>No sign-ups in {data.month}.</td></tr>
                  )}
                  {data.sources.map((row) => (
                    <React.Fragment key={row.source}>
                      <tr className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                          onClick={() => setOpen((o) => ({ ...o, [row.source]: !o[row.source] }))}>
                        <td className={`${td} font-mono flex items-center gap-1.5`}>
                          {open[row.source] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          {row.source}
                        </td>
                        <td className={td}>{row.signups}</td>
                        <td className={td}>{row.hosts}</td>
                        <td className={`${td} font-bold text-emerald-300`}>{row.qualifyingHosts}</td>
                        <td className={td}>{row.liveListingHosts}</td>
                        <td className={td}>{row.firstBookingHosts}</td>
                      </tr>
                      {open[row.source] && (
                        <tr className="border-b border-white/5 bg-black/20">
                          <td colSpan={6} className="px-6 py-3 space-y-3">
                            <div>
                              <div className={label}>By campaign / medium</div>
                              <div className="mt-1 text-xs text-zinc-300 space-y-0.5">
                                {row.campaigns.map((c) => (
                                  <div key={`${c.campaign}|${c.medium}`} className="font-mono">
                                    {c.campaign} · {c.medium}: {c.signups} sign-up(s), {c.qualifyingHosts} qualifying
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <div className={label}>
                                Qualifying accounts — check for duplicates before invoicing. Internal only: send partners the totals, never this list (Privacy Policy §2)
                              </div>
                              {row.qualifyingAccounts.length === 0 ? (
                                <div className="text-xs text-zinc-500 mt-1">None this month.</div>
                              ) : (
                                <div className="mt-1 text-xs text-zinc-300 space-y-0.5">
                                  {row.qualifyingAccounts.map((a) => (
                                    <div key={a.userId} className="font-mono">
                                      {a.userRef || a.userId} · {a.name} · {a.email}
                                      {a.campaign ? ` · ${a.campaign}` : ''}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-zinc-500">
              Qualifying host: {data.definitions?.qualifyingHost} Counted by account creation date; a host who
              starts their listing later appears when this month is re-run.
              {data.truncated && ' Report truncated — too many sign-ups this month.'}
            </p>
          </>
        )}
      </div>

      {/* ── Link builder ── */}
      <div className={`${card} p-5 space-y-4`}>
        <div className="text-sm font-bold text-white">Create a tracking link</div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p.name}
                    onClick={() => { setSource(p.source); setMedium(p.medium); setCampaign(p.campaign); }}
                    className="px-2.5 py-1 text-[11px] rounded-full border border-white/10 text-zinc-300 hover:bg-white/5">
              {p.name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <div className={label}>Page</div>
            <select value={page} onChange={(e) => setPage(e.target.value)} className={`${input} mt-1`}>
              {PAGES.map((p) => <option key={p.path} value={p.path}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <div className={label}>Source (who gets credit)</div>
            <input value={source} onChange={(e) => setSource(e.target.value)} className={`${input} mt-1`} placeholder="ygm" />
          </div>
          <div>
            <div className={label}>Medium (where it's posted)</div>
            <input value={medium} onChange={(e) => setMedium(e.target.value)} className={`${input} mt-1`} placeholder="linkedin" />
          </div>
          <div>
            <div className={label}>Campaign (optional)</div>
            <input value={campaign} onChange={(e) => setCampaign(e.target.value)} className={`${input} mt-1`} placeholder="founder-story" />
          </div>
        </div>
        {link && (
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs text-blue-200 bg-black/30 border border-white/10 rounded-lg px-3 py-2 break-all">{link}</code>
            <button onClick={copyLink}
                    className="px-3 py-2 text-xs font-semibold rounded-lg bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10 flex items-center gap-1.5">
              {copied ? <><CheckCircle2 size={13} className="text-emerald-400" /> Copied</> : <><Copy size={13} /> Copy</>}
            </button>
          </div>
        )}
        <p className="text-[11px] text-zinc-500">
          Letters, numbers and . _ - + only; spaces become dashes. The source must stay exactly the
          same on every link for a partner, or their sign-ups split across rows.
        </p>
      </div>
    </div>
  );
};

export default AdminAcquisitionSources;
